import type { CSSProperties } from "react";

/**
 * Palette default on `global_block_ref` (`elements.ts`) is an editor drop-zone,
 * same idea as `form_embed`'s dashed border. It must not paint on the public
 * page once the block content is inlined.
 */
const PLACEHOLDER_BORDER_COLOR = "#a78bfa";
const PLACEHOLDER_MIN_HEIGHT = "80px";
const PLACEHOLDER_PADDING = "16px";
const PLACEHOLDER_TEXT_COLOR = "#64748b";
const PLACEHOLDER_FONT_SIZE = "14px";

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readLeaf(value: unknown): string | undefined {
	if (typeof value === "string") {
		const trimmed = value.trim();
		return trimmed.length > 0 ? trimmed : undefined;
	}
	if (!isRecord(value)) {
		return undefined;
	}
	if ("desktop" in value) {
		return readLeaf(value.desktop);
	}
	if ("value" in value) {
		return readLeaf(value.value);
	}
	return undefined;
}

function isPlaceholderBorderColor(value: string | undefined): boolean {
	if (!value) {
		return false;
	}
	return value.toLowerCase() === PLACEHOLDER_BORDER_COLOR;
}

function isPlaceholderBorderStyle(value: string | undefined): boolean {
	return value === "dashed" || value === "dotted";
}

function paddingIsPlaceholder(padding: unknown): boolean {
	if (readLeaf(padding) === PLACEHOLDER_PADDING) {
		return true;
	}
	if (!isRecord(padding)) {
		return false;
	}
	if ("desktop" in padding) {
		return paddingIsPlaceholder(padding.desktop);
	}
	return (["top", "right", "bottom", "left"] as const).every(
		(side) => readLeaf(padding[side]) === PLACEHOLDER_PADDING
	);
}

/** Drop the editor chrome from a persisted builder `style` object. */
export function stripPersistedGlobalBlockPlaceholderStyle(
	style: unknown
): void {
	if (!isRecord(style)) {
		return;
	}
	const flatStyle = readLeaf(style.borderStyle);
	const flatColor = readLeaf(style.borderColor);
	const shorthand = typeof style.border === "string" ? style.border : "";
	const nestedBorder = isRecord(style.border) ? style.border : null;
	const nestedStyle = nestedBorder ? readLeaf(nestedBorder.style) : undefined;
	const nestedColor = nestedBorder ? readLeaf(nestedBorder.color) : undefined;
	const shorthandMatch =
		isPlaceholderBorderStyle(
			/dotted/i.test(shorthand)
				? "dotted"
				: /dashed/i.test(shorthand)
					? "dashed"
					: undefined
		) && /#a78bfa/i.test(shorthand);
	const matches =
		shorthandMatch ||
		(isPlaceholderBorderStyle(flatStyle) &&
			isPlaceholderBorderColor(flatColor)) ||
		(isPlaceholderBorderStyle(nestedStyle) &&
			isPlaceholderBorderColor(nestedColor));
	if (!matches) {
		return;
	}
	if (typeof style.border === "string" || nestedBorder) {
		delete style.border;
	}
	delete style.borderWidth;
	delete style.borderStyle;
	delete style.borderColor;
	if (readLeaf(style.minHeight) === PLACEHOLDER_MIN_HEIGHT) {
		delete style.minHeight;
	}
	const layout = isRecord(style.layout) ? style.layout : null;
	if (layout && readLeaf(layout.minHeight) === PLACEHOLDER_MIN_HEIGHT) {
		delete layout.minHeight;
	}
	if (paddingIsPlaceholder(style.padding)) {
		delete style.padding;
	}
	const spacing = isRecord(style.spacing) ? style.spacing : null;
	if (spacing && paddingIsPlaceholder(spacing.padding)) {
		delete spacing.padding;
	}
	if (readLeaf(style.color) === PLACEHOLDER_TEXT_COLOR) {
		delete style.color;
	}
	if (readLeaf(style.fontSize) === PLACEHOLDER_FONT_SIZE) {
		delete style.fontSize;
	}
	const typography = isRecord(style.typography) ? style.typography : null;
	if (typography) {
		if (readLeaf(typography.color) === PLACEHOLDER_TEXT_COLOR) {
			delete typography.color;
		}
		if (readLeaf(typography.fontSize) === PLACEHOLDER_FONT_SIZE) {
			delete typography.fontSize;
		}
	}
	if (readLeaf(style.borderRadius) === "8px") {
		delete style.borderRadius;
	}
}

/** Same chrome after it has been flattened to React CSS. */
export function cssWithoutGlobalBlockPlaceholder(
	css: CSSProperties
): CSSProperties {
	const shorthand = typeof css.border === "string" ? css.border : "";
	const borderStyle =
		css.borderStyle === "dashed" || css.borderStyle === "dotted"
			? css.borderStyle
			: /dotted/i.test(shorthand)
				? "dotted"
				: /dashed/i.test(shorthand)
					? "dashed"
					: undefined;
	const borderColor =
		typeof css.borderColor === "string" ? css.borderColor : undefined;
	const matches =
		isPlaceholderBorderStyle(borderStyle) &&
		(isPlaceholderBorderColor(borderColor) || /#a78bfa/i.test(shorthand));
	if (!matches) {
		return css;
	}
	const next: CSSProperties = { ...css };
	delete next.border;
	delete next.borderWidth;
	delete next.borderStyle;
	delete next.borderColor;
	if (next.minHeight === PLACEHOLDER_MIN_HEIGHT) {
		delete next.minHeight;
	}
	if (next.padding === PLACEHOLDER_PADDING) {
		delete next.padding;
	}
	if (next.color === PLACEHOLDER_TEXT_COLOR) {
		delete next.color;
	}
	if (next.fontSize === PLACEHOLDER_FONT_SIZE || next.fontSize === 14) {
		delete next.fontSize;
	}
	if (next.borderRadius === "8px") {
		delete next.borderRadius;
	}
	return next;
}
